/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-column.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import i18n from 'i18next';
import { SelectWithSearchDataArrayInterface } from '@components/core/select-with-categories/select-with-categories.interface';
import { isPanelScatterPlot } from '@utils-clean/panel-helpers';
import { useBoolean, useLocalState, useTranslation } from 'hooks';
import {
    DataReviewTableColumnLevel,
    DataReviewTableColumnType,
    DataReviewType,
    PanelFieldType,
    RowDataSourceTag,
} from 'types/graphql-global-types';
import { createArrayFromEnum } from 'utils/generator';
import { checkRequiredFields, validateFields } from 'utils/validation';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { FieldSelector, Formats } from 'views/components/central-monitoring/field-selector';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';
import { GET_CM2_PANELS } from '@queries/central-monitoring';
import {
    getCm2Panels_OpraQueries as getCm2PanelsResponse,
    getCm2PanelsVariables,
    panel_OpraQueries_panel_Panel,
} from '@queries/generated/central-monitoring.generated';

interface IAddColumn {
    currentColumn: any;
    open: boolean;
    onClose: (any: any) => any;
    currentTable: any;
}

const initialState = {
    heading: '',
    type: '' as any,
    dataField: '' as any,
    numeratorField: '' as any,
    denominatorField: '' as any,
    level: '' as any,
    dataSource: '' as any,
    panelId: '' as any,
    panelField: '' as any,
};

const initialErrors = {
    heading: '',
    type: '',
    dataField: '',
    numeratorField: '',
    denominatorField: '',
    level: '',
    dataSource: '',
    panelId: '',
    panelField: '',
};

const AddColumn = ({ currentColumn, open, onClose, currentTable }: IAddColumn) => {
    const { t } = useTranslation();
    const { state: localState } = useLocalState();

    const disabledSave = useBoolean(true);
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialErrors } as any);
    const [columnTypes, setColumnTypes] = useState([]);
    const [panelCalculationTypes, setPanelCalculationTypes] = useState([]);
    const [levels, setLevels] = useState([]);
    const [getPanels, { data: getPanelsRawData }] = useLazyQuery<getCm2PanelsResponse, getCm2PanelsVariables>(
        GET_CM2_PANELS,
    );
    const [filteredPanels, setFilteredPanels] = useState([]);

    const createArrayFromPanels = (panels: panel_OpraQueries_panel_Panel[], defaultValue?: string | number | null) => {
        const array: SelectWithSearchDataArrayInterface[] = [];

        panels.forEach((item: panel_OpraQueries_panel_Panel) => {
            array.push({
                id: item.id,
                text: item.plotTitle ?? '[Plot Title Missing]',
                item,
                active: item.active,
                type: item.panelType,
            });
        });
        array.unshift({
            id: defaultValue !== undefined ? defaultValue : '',
            text: i18n.t('ra:selectValue'),
        });
        return array;
    };

    useEffect(() => {
        if (localState?.selectedStudy?.useLatestCentralMonitoring) {
            getPanels({
                variables: {
                    organisationId: localState?.selectedOrganisation?.id,
                    studyId: localState?.selectedStudy?.id,
                },
            });
        }
    }, [localState?.selectedStudy, localState?.selectedOrganisation, getPanels]);

    useEffect(() => {
        if (getPanelsRawData) {
            //* Non-Trend Model Panels are where Panel = Model and Model = Negative Binomial OR Funnel OR Poisson OR TRI Operational
            const currentPanels =
                getPanelsRawData?.cm2panels
                    ?.filter((x) => x && x.active && isPanelScatterPlot(x))
                    ?.sort((a, b) => {
                        return a?.graph?.plotTitle?.localeCompare(b?.graph?.plotTitle);
                    }) ?? [];
            const filteredPanelsArray = createArrayFromPanels(currentPanels);
            setFilteredPanels(filteredPanelsArray);

            // based on the type of table we add the correct panels calculation types
            if (currentTable) {
                const allPanelCalculationTypes = createArrayFromEnum(PanelFieldType);
                switch (currentTable.type) {
                    case DataReviewType.STUDY:
                        setPanelCalculationTypes(
                            allPanelCalculationTypes?.filter(
                                (calcType: any) =>
                                    calcType.id != PanelFieldType.SITE_RATE &&
                                    calcType.id != PanelFieldType.COUNTRY_SITE_MEAN,
                            ),
                        );
                        break;
                    case DataReviewType.COUNTRY:
                        setPanelCalculationTypes(
                            allPanelCalculationTypes.filter((calcType: any) => calcType.id != PanelFieldType.SITE_RATE),
                        );
                        break;
                    case DataReviewType.DATA:
                        break;
                    default:
                        setPanelCalculationTypes(allPanelCalculationTypes);
                        break;
                }
            }
        }
    }, [getPanelsRawData]);

    useEffect(() => {
        if (currentTable) {
            const allLevels = createArrayFromEnum(DataReviewTableColumnLevel);
            const allColTypes = createArrayFromEnum(DataReviewTableColumnType);
            switch (currentTable.type) {
                case DataReviewType.STUDY: {
                    setLevels(
                        allLevels.filter(
                            (level: any) => level.id === '' || level.id === DataReviewTableColumnLevel.STUDY,
                        ),
                    );
                    // If the study is not using the latest central monitoring, we need to remove PANEL
                    const types = localState?.selectedStudy?.useLatestCentralMonitoring
                        ? allColTypes.filter(
                              (colType: any) =>
                                  colType.id !== DataReviewTableColumnType.COUNTRY_MEAN &&
                                  colType.id !== DataReviewTableColumnType.COUNTRY_MEDIAN &&
                                  colType.id !== DataReviewTableColumnType.SITE_MEAN &&
                                  colType.id !== DataReviewTableColumnType.SITE_MEDIAN &&
                                  colType.id !== DataReviewTableColumnType.PERCENTILE,
                          )
                        : allColTypes.filter(
                              (colType: any) =>
                                  colType.id !== DataReviewTableColumnType.COUNTRY_MEAN &&
                                  colType.id !== DataReviewTableColumnType.COUNTRY_MEDIAN &&
                                  colType.id !== DataReviewTableColumnType.SITE_MEAN &&
                                  colType.id !== DataReviewTableColumnType.SITE_MEDIAN &&
                                  colType.id !== DataReviewTableColumnType.PERCENTILE &&
                                  colType.id !== DataReviewTableColumnType.PANEL,
                          );
                    setColumnTypes(types);
                    break;
                }
                case DataReviewType.COUNTRY: {
                    setLevels(
                        allLevels.filter(
                            (level: any) =>
                                level.id === '' ||
                                level.id === DataReviewTableColumnLevel.STUDY ||
                                level.id === DataReviewTableColumnLevel.COUNTRY,
                        ),
                    );

                    // If the study is not using the latest central monitoring, we need to remove PANEL
                    const types = localState?.selectedStudy?.useLatestCentralMonitoring
                        ? allColTypes.filter(
                              (colType: any) =>
                                  colType.id !== DataReviewTableColumnType.PERCENTILE &&
                                  colType.id !== DataReviewTableColumnType.STANDARD_CALCULATION &&
                                  colType.id !== DataReviewTableColumnType.SITE_MEAN &&
                                  colType.id !== DataReviewTableColumnType.SITE_MEDIAN,
                          )
                        : allColTypes.filter(
                              (colType: any) =>
                                  colType.id !== DataReviewTableColumnType.PERCENTILE &&
                                  colType.id !== DataReviewTableColumnType.STANDARD_CALCULATION &&
                                  colType.id !== DataReviewTableColumnType.SITE_MEAN &&
                                  colType.id !== DataReviewTableColumnType.SITE_MEDIAN &&
                                  colType.id !== DataReviewTableColumnType.PANEL,
                          );
                    setColumnTypes(types);
                    break;
                }
                case DataReviewType.DATA: {
                    setLevels(
                        allLevels.filter(
                            (level: any) =>
                                level.id !== DataReviewTableColumnLevel.NONE &&
                                level.id !== DataReviewTableColumnLevel.SUBJECT,
                        ),
                    );
                    setColumnTypes(
                        allColTypes.filter(
                            (colType: any) =>
                                colType.id === '' ||
                                colType.id === DataReviewTableColumnType.STATIC ||
                                colType.id === DataReviewTableColumnType.STANDARD_CALCULATION,
                        ),
                    );
                    break;
                }
                case DataReviewType.SUBJECT: {
                    setLevels(allLevels.filter((level: any) => level.id !== DataReviewTableColumnLevel.NONE));
                    // If the study is not using the latest central monitoring, we need to remove PANEL
                    const types = localState?.selectedStudy?.useLatestCentralMonitoring
                        ? allColTypes
                        : allColTypes.filter((colType: any) => colType.id !== DataReviewTableColumnType.PANEL);
                    setColumnTypes(types);
                    break;
                }
                default: {
                    setLevels(
                        allLevels.filter(
                            (level: any) =>
                                level.id !== DataReviewTableColumnLevel.NONE &&
                                level.id !== DataReviewTableColumnLevel.SUBJECT,
                        ),
                    );
                    // If the study is not using the latest central monitoring, we need to remove PANEL
                    const types = localState?.selectedStudy?.useLatestCentralMonitoring
                        ? allColTypes
                        : allColTypes.filter((colType: any) => colType.id !== DataReviewTableColumnType.PANEL);
                    setColumnTypes(types);
                    break;
                }
            }
        }
    }, [currentTable]);

    const getRowDataSource = () => currentColumn.dataSource || state.dataSource || currentTable.dataSource;

    useEffect(() => {
        if (currentColumn)
            setState({
                heading: currentColumn.heading ?? state.heading,
                type: currentColumn.type ?? state.type,
                dataField: currentColumn.dataField ?? state.dataField,
                dataSource: getRowDataSource(),
                level: currentColumn.level ?? state.level ?? DataReviewTableColumnLevel.NONE,
                numeratorField: currentColumn.numeratorField ?? state.numeratorField,
                denominatorField: currentColumn.denominatorField ?? state.denominatorField,
                panelId: currentColumn.panelId ?? state.panelId,
                panelField: currentColumn.panelField ?? state.panelField,
            });
    }, [currentColumn]);

    const close = (s?: any) => {
        setState({ ...initialState });
        setErrors({ ...initialErrors });
        disabledSave.setTrue();
        if (s)
            onClose({
                ...s,
                level: !s.level.length ? null : s.level,
                dataSource: !s.dataSource.length ? null : s.dataSource,
                panelId: !s.panelId.length ? null : s.panelId,
                panelField: !s.panelField.length ? null : s.panelField,
            });
        else onClose(null);
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);
        if (field === 'type')
            setErrors({
                ...initialErrors,
            });
        else
            setErrors({
                ...errors,
                ...error,
            });
    };

    const onChangeDataSource = (value: string) => {
        disabledSave.setFalse();
        setState({
            ...state,
            dataSource: value,
            dataField: '',
        });

        validate(value, 'dataSource', 150, false);
    };

    const onChange = (name: string) => (event: any) => {
        disabledSave.setFalse();
        const length = 150;

        if (name === 'type')
            setState({
                ...state,
                [name]: event.target.value,
                dataSource: '',
                level: '',
                dataField: '',
                numeratorField: '',
                denominatorField: '',
                panelId: '',
                panelField: '',
            });
        else
            setState({
                ...state,
                [name]: event.target.value,
            });

        validate(event.target.value, name, length, false);
    };

    const onChangeField = (name: string) => (value: any) => {
        disabledSave.setFalse();

        setState({
            ...state,
            [name]: value,
        });

        validate(value, name, 150, false);
    };

    const submit = () => {
        const requiredFields = ['heading', 'type'];

        if (state.type === DataReviewTableColumnType.PERCENTAGE || state.type === DataReviewTableColumnType.RATE)
            requiredFields.push('dataSource', 'denominatorField', 'numeratorField');
        else if (
            state.type === DataReviewTableColumnType.MAX ||
            state.type === DataReviewTableColumnType.MIN ||
            state.type === DataReviewTableColumnType.SUM
        )
            requiredFields.push('level', 'dataSource', 'dataField');
        else if (state.type === DataReviewTableColumnType.COUNT) requiredFields.push('level', 'dataSource');
        else if (
            state.type === DataReviewTableColumnType.COUNTRY_MEAN ||
            state.type === DataReviewTableColumnType.COUNTRY_MEDIAN ||
            state.type === DataReviewTableColumnType.STUDY_MEAN ||
            state.type === DataReviewTableColumnType.STUDY_MEDIAN
        )
            requiredFields.push('dataSource', 'dataField');
        else if (state.type === DataReviewTableColumnType.PANEL) requiredFields.push('panelId', 'panelField');
        else requiredFields.push('dataField');

        if (checkRequiredFields({ fields: requiredFields, state, setErrors, errors })) {
            const column = {
                ...currentColumn,
                ...state,
            };
            if (!column.dataSource) column.dataSource = currentTable?.dataSource;

            close(column);
        }
    };

    let currentRowDataSource = '';
    if (currentTable)
        switch (currentTable.type) {
            case DataReviewType.SITE:
                currentRowDataSource = 'sitesAll';
                break;

            case DataReviewType.SUBJECT:
                currentRowDataSource = 'subjectsAll';
                break;

            case DataReviewType.COUNTRY:
            case DataReviewType.STUDY:
                currentRowDataSource = 'studyAll';
                break;

            default:
                currentRowDataSource = currentTable.dataSource;
                break;
        }

    return (
        <CustomDialog
            onClose={() => close()}
            aria-labelledby="add-table-title"
            fullWidth
            scroll="paper"
            maxWidth="md"
            open={open}
        >
            <DialogTitle id="add-table-title">
                {currentColumn ? t('admin:editColumn') : t('admin:addColumn')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent style={{ paddingTop: 15 }}>
                <div style={{ minHeight: 350 }}>
                    <div className="selects-wrapper">
                        <TextField
                            label={`${t('admin:columnHeading')}*`}
                            id="edit-column-heading"
                            fullWidth
                            value={state.heading}
                            onChange={onChange('heading')}
                            size="small"
                            placeholder={t('admin:enterColumnHeading')}
                            helperText={
                                <CharactersRemaining
                                    id="edit-column-heading-text"
                                    length={state.heading.length}
                                    max={150}
                                    error={errors.heading}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            error={!!errors.heading}
                            className="form-action-type mr"
                        />
                        <SelectWithCategories
                            label={`${t('admin:columnType')}*`}
                            dataArray={columnTypes}
                            onChange={onChange}
                            fieldSelect="type"
                            currentState={state.type}
                            searchable
                            filterable={false}
                            labelField="type"
                            showWarning={false}
                            customClassName="form-action-type"
                            error={errors.type}
                        />
                    </div>
                    {state.type === DataReviewTableColumnType.STATIC ? (
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <FieldSelector
                                    customClassName="edit-cm-dataPoint"
                                    fieldSelect="dataField"
                                    onChange={onChangeField('dataField')}
                                    label={`${t('admin:dataPoint')}*`}
                                    labelField="dataField"
                                    customId="row-dataField"
                                    value={state.dataField}
                                    error={errors.dataField}
                                    dataSource={currentRowDataSource}
                                    includeParentFields
                                    formats={[]}
                                    isCalculated={false}
                                    searchable
                                />
                            </FormControl>
                        </div>
                    ) : null}
                    {state.type === DataReviewTableColumnType.STANDARD_CALCULATION ? (
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <FieldSelector
                                    customClassName="edit-cm-dataPoint"
                                    fieldSelect="dataField"
                                    onChange={onChangeField('dataField')}
                                    label={`${t('admin:calculation')}*`}
                                    labelField="dataField"
                                    customId="row-dataField"
                                    value={state.dataField}
                                    error={errors.dataField}
                                    dataSource={currentRowDataSource}
                                    includeParentFields
                                    formats={[]}
                                    isCalculated
                                    searchable
                                />
                            </FormControl>
                        </div>
                    ) : null}
                    {state.type === DataReviewTableColumnType.PERCENTAGE ||
                    state.type === DataReviewTableColumnType.RATE ? (
                        <>
                            <div className="type-edc-wrapper mb-35">
                                <FormControl variant="outlined" className="form-control-type">
                                    <DataSourceSelector
                                        label={`${t('dataSource')}*`}
                                        onChange={onChangeDataSource}
                                        fieldSelect="dataSource"
                                        value={state.dataSource}
                                        labelField="dataSource"
                                        showDefault={false}
                                        searchable
                                        error={errors.dataSource}
                                        customClassName="form-action-type not-full"
                                        tag={RowDataSourceTag.DATA_TABLE}
                                    />
                                </FormControl>
                            </div>
                            <div className="type-edc-wrapper">
                                <FormControl variant="outlined" className="form-control-type mr">
                                    <FieldSelector
                                        customClassName="edit-cm-denominatorField"
                                        fieldSelect="denominatorField"
                                        onChange={onChangeField('denominatorField')}
                                        label={`${t('admin:denominator')}*`}
                                        labelField="denominatorField"
                                        customId="row-denominatorField"
                                        value={state.denominatorField}
                                        error={errors.denominatorField}
                                        dataSource={state.dataSource}
                                        formats={[Formats.Int, Formats.Float]}
                                        searchable
                                    />
                                </FormControl>
                                <FormControl variant="outlined" className="form-control-type">
                                    <FieldSelector
                                        customClassName="edit-cm-numeratorField"
                                        fieldSelect="numeratorField"
                                        onChange={onChangeField('numeratorField')}
                                        label={`${t('admin:numerator')}*`}
                                        labelField="numeratorField"
                                        customId="row-numeratorField"
                                        value={state.numeratorField}
                                        error={errors.numeratorField}
                                        dataSource={state.dataSource ?? ''}
                                        formats={[Formats.Int, Formats.Float]}
                                        searchable
                                    />
                                </FormControl>
                            </div>
                        </>
                    ) : null}
                    {state.type === DataReviewTableColumnType.MAX ||
                    state.type === DataReviewTableColumnType.MIN ||
                    state.type === DataReviewTableColumnType.SUM ? (
                        <>
                            <div className="type-edc-wrapper mb-35">
                                <SelectWithCategories
                                    label={`${t('level')}*`}
                                    dataArray={levels}
                                    onChange={onChange}
                                    fieldSelect="level"
                                    currentState={state.level}
                                    searchable
                                    filterable={false}
                                    labelField="level"
                                    showWarning={false}
                                    customClassName="form-action-type not-full"
                                    error={errors.level}
                                />
                            </div>
                            <div className="type-edc-wrapper">
                                <DataSourceSelector
                                    label={`${t('dataSource')}*`}
                                    onChange={onChangeDataSource}
                                    fieldSelect="dataSource"
                                    value={state.dataSource}
                                    labelField="dataSource"
                                    showDefault={false}
                                    searchable
                                    error={errors.dataSource}
                                    customClassName="form-action-type mr"
                                    tag={RowDataSourceTag.DATA_TABLE}
                                />
                                <FormControl variant="outlined" className="form-control-type">
                                    <FieldSelector
                                        customClassName="edit-cm-dataPoint"
                                        fieldSelect="dataField"
                                        onChange={onChangeField('dataField')}
                                        label={`${t('admin:dataPoint/Calculation')}*`}
                                        labelField="dataField"
                                        customId="row-dataField"
                                        value={state.dataField}
                                        error={errors.dataField}
                                        dataSource={state.dataSource ?? ''}
                                        formats={
                                            state.type === DataReviewTableColumnType.SUM
                                                ? [Formats.Int, Formats.Float]
                                                : [Formats.Int, Formats.Float, Formats.Date, Formats.PartialDate]
                                        }
                                        searchable
                                    />
                                </FormControl>
                            </div>
                        </>
                    ) : null}
                    {state.type === DataReviewTableColumnType.COUNT ? (
                        <div className="type-edc-wrapper">
                            <SelectWithCategories
                                label={`${t('level')}*`}
                                dataArray={levels}
                                onChange={onChange}
                                fieldSelect="level"
                                currentState={state.level}
                                searchable
                                filterable={false}
                                labelField="level"
                                showWarning={false}
                                customClassName="form-action-type mr"
                                error={errors.level}
                            />
                            <DataSourceSelector
                                label={`${t('dataSource')}*`}
                                onChange={onChangeDataSource}
                                fieldSelect="dataSource"
                                value={state.dataSource}
                                labelField="dataSource"
                                showDefault={false}
                                searchable
                                error={errors.dataSource}
                                customClassName="form-action-type"
                                tag={RowDataSourceTag.DATA_TABLE}
                            />
                        </div>
                    ) : null}
                    {state.type === DataReviewTableColumnType.PERCENTILE ? (
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type">
                                <FieldSelector
                                    customClassName="edit-cm-dataPoint"
                                    fieldSelect="dataField"
                                    onChange={onChangeField('dataField')}
                                    label={`${t('admin:dataPoint/Calculation')}*`}
                                    labelField="dataField"
                                    customId="row-dataField"
                                    value={state.dataField}
                                    error={errors.dataField}
                                    dataSource={currentRowDataSource}
                                    formats={[Formats.Int, Formats.Float]}
                                    searchable
                                />
                            </FormControl>
                        </div>
                    ) : null}
                    {state.type === DataReviewTableColumnType.COUNTRY_MEDIAN ||
                    state.type === DataReviewTableColumnType.COUNTRY_MEAN ||
                    state.type === DataReviewTableColumnType.SITE_MEAN ||
                    state.type === DataReviewTableColumnType.SITE_MEDIAN ||
                    state.type === DataReviewTableColumnType.STUDY_MEAN ||
                    state.type === DataReviewTableColumnType.STUDY_MEDIAN ? (
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type mr">
                                <DataSourceSelector
                                    label={`${t('dataSource')}*`}
                                    onChange={onChangeDataSource}
                                    fieldSelect="dataSource"
                                    value={state.dataSource}
                                    labelField="dataSource"
                                    showDefault={false}
                                    searchable
                                    error={errors.dataSource}
                                    customClassName="form-action-type mr"
                                    tag={RowDataSourceTag.DATA_TABLE}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type">
                                <FieldSelector
                                    customClassName="edit-cm-dataPoint"
                                    fieldSelect="dataField"
                                    onChange={onChangeField('dataField')}
                                    label={`${t('admin:dataPoint/Calculation')}*`}
                                    labelField="dataField"
                                    customId="row-dataField"
                                    value={state.dataField}
                                    error={errors.dataField}
                                    dataSource={state.dataSource}
                                    formats={[Formats.Int, Formats.Float]}
                                    searchable
                                />
                            </FormControl>
                        </div>
                    ) : null}
                    {state.type === DataReviewTableColumnType.PANEL ? (
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type mr">
                                <SelectWithCategories
                                    label={`${t('admin:panel')}*`}
                                    dataArray={filteredPanels}
                                    onChange={onChange}
                                    fieldSelect="panelId"
                                    currentState={state.panelId}
                                    searchable
                                    filterable={false}
                                    labelField="panelId"
                                    showWarning={false}
                                    customClassName="form-action-type mr"
                                    error={errors.panelId}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type">
                                <SelectWithCategories
                                    label={`${t('admin:calculation')}*`}
                                    dataArray={panelCalculationTypes}
                                    onChange={onChange}
                                    fieldSelect="panelField"
                                    currentState={state.panelField}
                                    searchable
                                    filterable={false}
                                    labelField="panelField"
                                    showWarning={false}
                                    customClassName="form-action-type mr"
                                    error={errors.panelField}
                                />
                            </FormControl>
                        </div>
                    ) : null}
                </div>
                <DialogActions className="actions-info-wrapper-edit-cv flex-end">
                    <SaveButton
                        className="edit-cv-update-btn"
                        id="edit-observation-save"
                        text={currentColumn ? 'common:update' : 'add'}
                        disabled={Object.keys(errors).filter((item) => errors[item]).length > 0 || disabledSave.value}
                        onClick={submit}
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default AddColumn;
